import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGBondsAndDebentureDetailsForm } from ".";
import { IBondsDebentureForm, IFormITR1, IReviewCapitalGains } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const CGBondsAndDebentureDetails: React.FC = () => {
	const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<IBondsDebentureForm | null>(null);
	const [activeItem, setActiveItem] = useState<IBondsDebentureForm | null>(null);

	// used in both edit and delete cases
	const [activeItemIdx, setActiveItemIdx] = useState<string>("");

	const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
	const BondsAndDebentureDetails = cloneDeep(values.IncomeDetails?.BondsAndDebenture || []);

	const handleDeleteInterestItem = async () => {
		BondsAndDebentureDetails.splice(Number(activeItemIdx), 1);
		setFieldValue("IncomeDetails.BondsAndDebenture", BondsAndDebentureDetails);
		setShowDeleteModal(() => null);
		setActiveItem(() => null);
		setActiveItemIdx(() => "");
		handleTotalCalculation();
	};

	const handleTotalCalculation = () => {
		const BondsDebentureGains =
			BondsAndDebentureDetails?.reduce((acc, curr) => {
				const value = parseInt(curr.Gains);
				return acc + (isNaN(value) ? 0 : value);
			}, 0) || 0;

		const CapitalGains: IReviewCapitalGains = {
			...values.CapitalGains,
			TotalBondsDebentureGain: BondsDebentureGains
		};

		const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
			return acc + curr;
		}, 0);

		const DATA = {
			CapitalGains: CapitalGains,
			TotalCapitalGains: TotalCapitalGains
		};

		setValues({ ...cloneDeep(values), ...DATA });
		setFieldValue("IncomeDetails.BondsAndDebenture", BondsAndDebentureDetails);
		setFieldValue("LastSavedTimeStamp", new Date().getTime());
	};

	return (
		<>
			<section className="mb-5">
				<div className="section-title-wrap">
					<h5>Bonds and Debenture</h5>
					<PreventEditComponent>
						<Button
							variant="secondary"
							size="sm"
							onClick={() => {
								setShowAddFormCanvas(() => true);
							}}
						>
							<FASvgIcon icon={faPlus} />
							Add
						</Button>
					</PreventEditComponent>
				</div>

				<dl className="eds-list eds-dl eds-inline-between">
					{BondsAndDebentureDetails.map((data, index) => {
						return (
							<div key={`any-asset-${index}`} className="eds-list--item">
								<dt>
									{data.DescriptionAssetSold} ({data.TypeBonds})
								</dt>
								<dd>
									<span>
										<PriceDisplay price={data?.Gains} />
									</span>
									<Button
										variant="link"
										onClick={() => {
											setShowAddFormCanvas(() => true);
											setActiveItem(() => data);
											setActiveItemIdx(() => String(index));
										}}
									>
										View
										<PreventEditComponent>
											<>/Edit</>
										</PreventEditComponent>
									</Button>
									<PreventEditComponent>
										<Button
											variant="transparent"
											className="icon-only"
											onClick={() => {
												setShowDeleteModal(() => data);
												setActiveItemIdx(() => String(index));
											}}
										>
											<FASvgIcon icon={faTrash} />
										</Button>
									</PreventEditComponent>
								</dd>
							</div>
						);
					})}
				</dl>
			</section>

			<CGBondsAndDebentureDetailsForm
				showCanvas={showAddFormCanvas}
				handleCloseBtnPress={(data) => {
					if (data) {
						if (activeItem) {
							BondsAndDebentureDetails.splice(Number(activeItemIdx), 1, data);
						} else {
							BondsAndDebentureDetails.push(data);
						}
						handleTotalCalculation();
					}
					setShowAddFormCanvas(() => false);
					setActiveItem(() => null);
					setActiveItemIdx(() => "");
				}}
				formValues={activeItem}
			/>

			<ConfirmationModal
				showModal={!!showDeleteModal}
				iconColorClass="text-danger"
				title="Are you sure?"
				message={<p>You want to delete this</p>}
				onYesBtnClick={() => handleDeleteInterestItem()}
				onNoBtnClick={() => setShowDeleteModal(() => null)}
				icon={faTrashAlt}
			/>
		</>
	);
};

export default CGBondsAndDebentureDetails;
